<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import CalibrationPanel from '../components/MapViewer/CalibrationPanel.vue'
import PixiMap from '../components/MapViewer/PixiMap.vue'
import UploadPanel from '../components/UploadPanel/UploadPanel.vue'
import { useGameStore } from '../stores/game'

const store = useGameStore()
const selectedBiome = ref<number | null>(null)

watch(
  () => store.game,
  (game) => {
    selectedBiome.value = game?.player.biome ?? game?.maps[0]?.biome ?? null
  }
)

const selectedMap = computed(
  () => store.maps.find((map) => map.biome === selectedBiome.value) ?? store.maps[0] ?? null
)
const selectedCalibration = computed(
  () => store.game?.calibrations.find(
    (calibration) => calibration.biome === selectedMap.value?.biome
  ) ?? null
)
</script>

<template>
  <div class="page-stack">
    <section class="page-heading">
      <div>
        <p class="eyebrow">地图工作台</p>
        <h1>真实存档地图</h1>
        <p>直接读取 v4 单文件中的地图像素、地区、据点和玩家位置；缺少像素时会明确降级。</p>
      </div>
      <div class="status-card" :class="{ loaded: store.loaded }">
        <span>{{ store.loaded ? 'v4 数据已载入' : '等待数据' }}</span>
        <strong v-if="store.player">
          {{ store.player.x.toFixed(1) }}, {{ store.player.y.toFixed(1) }}
        </strong>
        <strong v-else>—</strong>
        <small>世界坐标</small>
      </div>
    </section>

    <UploadPanel />

    <template v-if="store.game">
      <section v-if="store.game.warnings.length" class="warning-panel">
        <strong>证据提示</strong>
        <p v-for="warning in store.game.warnings" :key="warning">{{ warning }}</p>
      </section>

      <section class="summary-grid">
        <article>
          <span>活跃任务</span>
          <strong>{{ store.quests.length }}</strong>
          <small>{{ store.readyQuestCount }} 个目标已满足</small>
        </article>
        <article>
          <span>据点状态</span>
          <strong>{{ store.outposts.length }}</strong>
          <small>{{ store.outposts.filter((item) => item.visible).length }} 个可见</small>
        </article>
        <article>
          <span>材料缺口</span>
          <strong>{{ store.missingResourceCount }}</strong>
          <small>由活跃任务目标合并计算</small>
        </article>
      </section>

      <div v-if="store.maps.length > 1" class="map-tabs">
        <button
          v-for="map in store.maps"
          :key="map.fileName"
          type="button"
          :class="{ active: selectedMap?.fileName === map.fileName }"
          @click="selectedBiome = map.biome"
        >
          地区 {{ map.biome }}
          <small>{{ map.pixelStatus === 'ready' ? '有像素' : '仅元数据' }}</small>
        </button>
      </div>

      <PixiMap
        v-if="store.player"
        :map="selectedMap"
        :calibration="selectedCalibration"
        :player="store.player"
        :outposts="store.outposts"
        :quests="store.quests"
        :route="store.route"
      />

      <CalibrationPanel
        v-if="selectedMap && selectedCalibration && store.player"
        :map="selectedMap"
        :player="store.player"
        :calibration="selectedCalibration"
        @save="store.setCalibration"
      />

      <section v-if="store.route" class="content-panel route-panel">
        <div class="section-heading">
          <div>
            <p class="eyebrow">建议顺序</p>
            <h2>语义路线</h2>
          </div>
          <span>{{ store.route.coordinateStatus }}</span>
        </div>
        <ol class="route-list">
          <li v-for="stop in store.route.stops.slice(0, 8)" :key="`${stop.order}-${stop.questId}`">
            <b>{{ stop.order }}</b>
            <div>
              <strong>{{ stop.title }}</strong>
              <small>{{ stop.detail }}</small>
            </div>
          </li>
        </ol>
      </section>
    </template>
  </div>
</template>
